import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { Status, Tooltip } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useKlaviyoCRUDActions } from '@mm/state/actions/KlaviyoCRUDActions';
import {
  ConnectionModel,
  klaviyoTokenErrorMsg,
} from '@mm/state/model/ConnectionModel';
import classNames from 'classnames';
import React, { MouseEvent } from 'react';
import { PrimaryButton } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { colors } from '../../../../../styles/colors';
import { Help } from './KlaviyoHelpLink';
import styles from './KlaviyoIntegrations.module.css';
import { useConnectOauthToken } from './use-connect-oauth-token';

type Props = {
  connection: ConnectionModel | undefined;
  helpLink: string;
  activeFbPageId: number;
  hasNativeIntegrationsFeature: boolean | undefined;
  getLists: (webhookId: number) => void;
  showUpgradeModal: (onSuccessfulBilling?: () => void) => void;
};

export const KlaviyoOauthTokenConnection = (props: Props) => {
  const {
    connection: klaviyoConnection,
    helpLink,
    activeFbPageId,
    hasNativeIntegrationsFeature,
    getLists,
    showUpgradeModal,
  } = props;

  const { retrieveById } = useConnectionCRUDActions();
  const { revoke } = useKlaviyoCRUDActions();

  const connectOauthToken = useConnectOauthToken(
    'klaviyoConnect',
    'klaviyoTokenReceived',
    () => {
      if (klaviyoConnection) {
        retrieveById(activeFbPageId, klaviyoConnection.id);
        getLists(klaviyoConnection.id);
      }
    },
  );

  const revokeToken = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (klaviyoConnection) {
      const revoked = await revoke(klaviyoConnection.id);
      if (revoked) {
        await retrieveById(activeFbPageId, klaviyoConnection.id);
      }
    }
  };

  const connectToken = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    await connectOauthToken(
      activeFbPageId,
      !!hasNativeIntegrationsFeature,
      showUpgradeModal,
      klaviyoConnection,
      {
        name: 'Klaviyo Connection',
        connectionKind: 'klaviyo',
      },
    );
  };

  const missingScopes =
    klaviyoConnection?.klaviyoOauthToken?.missingScopes || [];

  return (
    <div className={classNames(styles.box, styles.boxWithHelp)}>
      <div className={styles.boxContent}>
        <div className={styles.heading}>Connect to Klaviyo Directly</div>
        <div className={styles.connectionButtonsWrapper}>
          {!klaviyoConnection?.klaviyoOauthToken ? (
            <PrimaryButton
              // className={styles.primaryButton}
              className={classNames(
                'integrations__header-add-button-secondary',
                'integrations__header-button-no-margin',
              )}
              onClick={e => connectToken(e)}
            >
              Connect
            </PrimaryButton>
          ) : (
            <>
              {klaviyoConnection?.klaviyoOauthToken?.errorInfo ? (
                <Tooltip
                  anchorElement={
                    <Status
                      status="Failed to connect"
                      color={colors.red}
                      className={styles.oauthStatus}
                    />
                  }
                  useHover
                  css={{
                    container: styles.tooltipContainer,
                  }}
                  withoutLine
                  position="top"
                >
                  {klaviyoTokenErrorMsg(
                    klaviyoConnection.klaviyoOauthToken.errorInfo,
                  )}
                </Tooltip>
              ) : missingScopes.length > 0 ? (
                <Tooltip
                  anchorElement={
                    <Status
                      status="Connected"
                      color={colors.orange}
                      className={styles.oauthStatus}
                    />
                  }
                  useHover
                  css={{
                    container: styles.tooltipContainer,
                  }}
                  withoutLine
                  position="top"
                >
                  {`Some of the scopes the application requires are missing:
                    ${missingScopes.join('; ')}`}
                </Tooltip>
              ) : (
                <Status
                  status="Connected"
                  color={colors.green}
                  className={styles.oauthStatus}
                />
              )}
              <PrimaryButton
                className={classNames(
                  'integrations__header-add-button-secondary',
                  'integrations__header-button-no-margin',
                )}
                onClick={e => connectToken(e)}
                leftIcon={<SyncOutlined />}
              >
                Reconnect
              </PrimaryButton>
              <PrimaryButton
                className={classNames(
                  'integrations__header-add-button-secondary',
                  'integrations__header-button-no-margin',
                )}
                onClick={e => revokeToken(e)}
                leftIcon={<CloseOutlined />}
              >
                Disconnect
              </PrimaryButton>
            </>
          )}
        </div>
      </div>
      <Help link={helpLink} />
    </div>
  );
};
